/**
 * Team File System Operations
 * Thin wrapper around core/fs-operations for team shared datadrive
 */

import { TeamRole } from '@/consts/team'
import { TeamFSContext } from '@/pages/data-mgr/core/context'
import * as fsOps from '@/pages/data-mgr/core/fs-operations'
import { FSListResponse, IFile, TeamFSParams } from '@/pages/data-mgr/core/types'

// Re-export types for backward compatibility
export type { IFile, TeamFSParams }

/**
 * Get or create Team context for given role and team
 */
const contextCache = new Map<string, TeamFSContext>()
function getContext(userRole: TeamRole, teamId: number): TeamFSContext {
  const key = `${userRole}-${teamId}`
  let ctx = contextCache.get(key)
  if (!ctx) {
    ctx = new TeamFSContext(userRole, teamId)
    contextCache.set(key, ctx)
  }
  return ctx
}

/**
 * List directory contents
 */
export async function ls(params: { path: string } & TeamFSParams): Promise<FSListResponse> {
  const { userRole, teamId, ...fsParams } = params
  return fsOps.ls(getContext(userRole, teamId), fsParams)
}

/**
 * Move or rename file/folder
 */
export async function mv(params: { path: string; target: string } & TeamFSParams): Promise<void> {
  const { userRole, teamId, ...fsParams } = params
  return fsOps.mv(getContext(userRole, teamId), fsParams)
}

/**
 * Download file
 */
export async function download(params: { path: string } & TeamFSParams): Promise<void> {
  const { userRole, teamId, ...fsParams } = params
  return fsOps.download(getContext(userRole, teamId), fsParams)
}

/**
 * Delete file or folder
 */
export async function rm(params: { path: string } & TeamFSParams): Promise<void> {
  const { userRole, teamId, ...fsParams } = params
  return fsOps.rm(getContext(userRole, teamId), fsParams)
}

/**
 * Create folder
 */
export async function mkdir(params: { path: string } & TeamFSParams): Promise<void> {
  const { userRole, teamId, ...fsParams } = params
  return fsOps.mkdir(getContext(userRole, teamId), fsParams)
}

/**
 * Rename file or folder
 */
export async function rename(params: { path: string; name: string } & TeamFSParams): Promise<void> {
  const { userRole, teamId, ...fsParams } = params
  return fsOps.rename(getContext(userRole, teamId), fsParams)
}

/**
 * Download multiple files as a ZIP archive
 */
export async function downloadFilesAsZip(params: { files: IFile[] } & TeamFSParams): Promise<void> {
  const { userRole, teamId, ...fsParams } = params
  return fsOps.downloadAsZip(getContext(userRole, teamId), fsParams)
}
